/**
 * API Client
 * Handles all backend communication with security measures
 */

import axios, { AxiosInstance } from 'axios'
import type { BlogPost, Competition, CompetitionFixture, Fixture, Player, PlayerComment } from '../types'
import { getCompetitionPolicyViolation } from './competitions'

class APIClient {
  private client: AxiosInstance
  private apiUrl: string
  private apiKey: string
  private enabled: boolean
  private cachedToken: string | null = null

  constructor() {
    // Use new Supabase environment variables, fallback to legacy ones for compatibility
    this.apiUrl = import.meta.env.VITE_SUPABASE_URL || import.meta.env.VITE_API_URL || ''
    this.apiKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_API_KEY || ''

    this.enabled = Boolean(this.apiUrl && this.apiKey)

    if (!this.enabled) {
      console.warn('Legacy backend API not configured: VITE_API_URL or VITE_API_KEY is missing. API client methods will throw a clear error. The app will use Supabase directly when available.')
    }

    this.client = axios.create({
      baseURL: this.apiUrl ? `${this.apiUrl}/rest/v1` : undefined,
      headers: {
        'apikey': this.apiKey,
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    })

    // Request Interceptor to inject the latest session token
    this.client.interceptors.request.use(
      async (config) => {
        if (this.cachedToken) {
          config.headers['Authorization'] = `Bearer ${this.cachedToken}`
        } else {
          config.headers['Authorization'] = `Bearer ${this.apiKey}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          console.error('API Error Response:', error.response.status, error.response.data)
        } else {
          console.error('API Error:', error.message)
        }
        return Promise.reject(error)
      }
    )
  }

  setToken(token: string | null) {
    this.cachedToken = token
    if (token) {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${this.apiKey}`
    }
  }

  private ensureEnabled() {
    if (!this.enabled) {
      throw new Error('Legacy backend API is not configured (VITE_API_URL / VITE_API_KEY). Configure these in your environment or rely on Supabase client methods.')
    }
  }

  private getClient(): AxiosInstance {
    return this.client
  }

  private stripCareerHistory(payload: any) {
    if (!payload || typeof payload !== 'object') return payload
    const { career_history, ...rest } = payload
    return rest
  }

  private stripCompetitionPerformance(payload: any) {
    if (!payload || typeof payload !== 'object') return payload
    const { competition_performances, ...rest } = payload
    return rest
  }

  private shouldRetryWithoutCareerHistory(error: any) {
    const message = `${error?.response?.data?.message || error?.message || ''}`.toLowerCase()
    return message.includes('career_history') || message.includes('does not exist') || message.includes('schema cache')
  }

  private shouldRetryWithoutCompetitionPerformance(error: any) {
    const message = `${error?.response?.data?.message || error?.message || ''}`.toLowerCase()
    return message.includes('competition_performances') || message.includes('does not exist') || message.includes('schema cache')
  }

  private isUuid(value: any) {
    return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
  }

  private extractYouTubeId(url: string): string | null {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ]
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) return match[1]
    }
    return null
  }

  private async getCareerHistory(playerIds: string[]) {
    this.ensureEnabled()
    if (!playerIds.length) return []

    const response = await this.client.get(
      `/player_career_history?player_id=in.(${playerIds.join(',')})&order=created_at.asc`
    )
    return Array.isArray(response.data) ? response.data : []
  }

  private normalizeCareerHistory(rows: any[]) {
    return rows.map((row) => ({
      id: row.id,
      player_id: row.player_id,
      club: row.club || '',
      years: row.years || '',
      goals: row.goals ?? undefined,
      assists: row.assists ?? undefined,
      transaction_fee: row.transaction_fee ?? undefined,
    }))
  }

  private async getCompetitionPerformances(playerIds: string[]) {
    this.ensureEnabled()
    if (!playerIds.length) return []

    const response = await this.client.get(
      `/player_competition_performances?player_id=in.(${playerIds.join(',')})&order=created_at.asc`
    )
    return Array.isArray(response.data) ? response.data : []
  }

  private normalizeCompetitionPerformances(rows: any[]) {
    return rows.map((row) => ({
      id: row.id,
      player_id: row.player_id,
      competition_id: row.competition_id || undefined,
      competition_name: row.competition_name || 'Unknown Competition',
      appearance_date: row.appearance_date || undefined,
      opponent: row.opponent || undefined,
      venue: row.venue || undefined,
      minutes: Number(row.minutes || 0),
      goals: Number(row.goals || 0),
      assists: Number(row.assists || 0),
      shots: row.shots === undefined || row.shots === null ? undefined : Number(row.shots),
      key_passes: row.key_passes === undefined || row.key_passes === null ? undefined : Number(row.key_passes),
      dribbles: row.dribbles === undefined || row.dribbles === null ? undefined : Number(row.dribbles),
      tackles: row.tackles === undefined || row.tackles === null ? undefined : Number(row.tackles),
      rating: row.rating === undefined || row.rating === null ? undefined : Number(row.rating),
      notes: row.notes || undefined,
      created_by: row.created_by || undefined,
      created_at: row.created_at || undefined,
      updated_at: row.updated_at || undefined,
    }))
  }

  private async attachCareerHistory<T extends { id: string; career_history?: any[] }>(players: T[]) {
    this.ensureEnabled()
    if (!players.length) return players

    try {
      const historyRows = await this.getCareerHistory(players.map((player) => player.id))
      const grouped = new Map<string, any[]>()

      historyRows.forEach((row: any) => {
        const list = grouped.get(row.player_id) || []
        list.push(row)
        grouped.set(row.player_id, list)
      })

      return players.map((player) => ({
        ...player,
        career_history: this.normalizeCareerHistory(grouped.get(player.id) || []),
      })) as Array<T & { career_history: any[] }>
    } catch (error) {
      // Keep the player payload usable if the child table has not been created yet.
      return players.map((player) => ({ ...player, career_history: player.career_history || [] })) as Array<T & { career_history: any[] }>
    }
  }

  private async attachCompetitionPerformances<T extends { id: string; competition_performances?: any[] }>(players: T[]) {
    this.ensureEnabled()
    if (!players.length) return players

    try {
      const performanceRows = await this.getCompetitionPerformances(players.map((player) => player.id))
      const grouped = new Map<string, any[]>()

      performanceRows.forEach((row: any) => {
        const list = grouped.get(row.player_id) || []
        list.push(row)
        grouped.set(row.player_id, list)
      })

      return players.map((player) => ({
        ...player,
        competition_performances: this.normalizeCompetitionPerformances(grouped.get(player.id) || []),
      })) as Array<T & { competition_performances: any[] }>
    } catch (error) {
      return players.map((player) => ({ ...player, competition_performances: player.competition_performances || [] })) as Array<T & { competition_performances: any[] }>
    }
  }

  private async syncCareerHistory(playerId: string, history: any[]) {
    this.ensureEnabled()
    const client = this.getClient()
    const existing = await this.getCareerHistory([playerId])
    const incomingIds = new Set(history.map((entry) => entry.id).filter(Boolean))
    const removedIds = existing.map((row: any) => row.id).filter((id: string) => !incomingIds.has(id))

    if (!history.length) {
      if (existing.length) {
        await client.delete(`/player_career_history?player_id=eq.${playerId}`)
      }
      return []
    }

    const rows = history.map((entry) => ({
      id: entry.id || crypto.randomUUID(),
      player_id: playerId,
      club: entry.club || '',
      years: entry.years || '',
      goals: entry.goals === undefined || entry.goals === null || entry.goals === '' ? null : Number(entry.goals),
      assists: entry.assists === undefined || entry.assists === null || entry.assists === '' ? null : Number(entry.assists),
      transaction_fee: entry.transaction_fee?.trim() ? entry.transaction_fee.trim() : null,
      updated_at: new Date().toISOString(),
    }))

    const response = await client.post('/player_career_history', rows, {
      headers: { 'Prefer': 'resolution=merge-duplicates,return=representation' }
    })

    if (removedIds.length) {
      await client.delete(`/player_career_history?id=in.(${removedIds.join(',')})`)
    }

    return response.data || rows
  }

  private async syncCompetitionPerformances(playerId: string, performances: any[]) {
    this.ensureEnabled()
    const client = this.getClient()
    const existing = await this.getCompetitionPerformances([playerId])
    const incomingIds = new Set(performances.map((entry) => entry.id).filter(Boolean))
    const removedIds = existing.map((row: any) => row.id).filter((id: string) => !incomingIds.has(id))

    if (!performances.length) {
      if (existing.length) {
        await client.delete(`/player_competition_performances?player_id=eq.${playerId}`)
      }
      return []
    }

    const rows = performances.map((entry) => ({
      id: entry.id || crypto.randomUUID(),
      player_id: playerId,
      competition_id: entry.competition_id || null,
      competition_name: entry.competition_name || entry.competition || 'Unknown Competition',
      appearance_date: entry.appearance_date || null,
      opponent: entry.opponent || null,
      venue: entry.venue || null,
      minutes: entry.minutes === undefined || entry.minutes === null || entry.minutes === '' ? 0 : Number(entry.minutes),
      goals: entry.goals === undefined || entry.goals === null || entry.goals === '' ? 0 : Number(entry.goals),
      assists: entry.assists === undefined || entry.assists === null || entry.assists === '' ? 0 : Number(entry.assists),
      shots: entry.shots === undefined || entry.shots === null || entry.shots === '' ? null : Number(entry.shots),
      key_passes: entry.key_passes === undefined || entry.key_passes === null || entry.key_passes === '' ? null : Number(entry.key_passes),
      dribbles: entry.dribbles === undefined || entry.dribbles === null || entry.dribbles === '' ? null : Number(entry.dribbles),
      tackles: entry.tackles === undefined || entry.tackles === null || entry.tackles === '' ? null : Number(entry.tackles),
      rating: entry.rating === undefined || entry.rating === null || entry.rating === '' ? null : Number(entry.rating),
      notes: entry.notes?.trim() ? entry.notes.trim() : null,
      updated_at: new Date().toISOString(),
    }))

    const invalidCompetition = rows.find((row) => getCompetitionPolicyViolation(row.competition_name))
    if (invalidCompetition) {
      throw new Error(getCompetitionPolicyViolation(invalidCompetition.competition_name))
    }

    const response = await client.post('/player_competition_performances', rows, {
      headers: { 'Prefer': 'resolution=merge-duplicates,return=representation' }
    })

    if (removedIds.length) {
      await client.delete(`/player_competition_performances?id=in.(${removedIds.join(',')})`)
    }

    return response.data || rows
  }

  // Player comments (disabled)
  async getPlayerComments(playerId: string): Promise<PlayerComment[]> {
    // Comments system is disabled — return empty list.
    void playerId
    return []
  }

  async createPlayerComment(playerId: string, comment: {
    author_id: string
    author_name: string
    author_role: 'scout' | 'club' | 'admin'
    comment: string
  }) {
    // Comments creation is disabled.
    void playerId
    void comment
    return null
  }

  async deletePlayerComment(id: string) {
    // Comments deletion is disabled — no-op and report success.
    void id
    return true
  }

  // Players
  async getPlayers(filters?: Record<string, string>): Promise<Player[]> {
    this.ensureEnabled()
    try {
      let query = ''
      if (filters) {
        query = '?' + new URLSearchParams(filters).toString()
      }
      const response = await this.client.get(`/players${query}`)
      const withCareerHistory = await this.attachCareerHistory(Array.isArray(response.data) ? response.data : [])
      return await this.attachCompetitionPerformances(withCareerHistory as Player[]) as Player[]
    } catch (error) {
      throw new Error('Failed to fetch players')
    }
  }

  async getPlayer(id: string): Promise<Player | undefined> {
    this.ensureEnabled()
    try {
      const response = await this.client.get(`/players?id=eq.${id}`)
      const player = response.data?.[0]
      if (!player) return undefined
      const withCareerHistory = await this.attachCareerHistory([player])
      const withPerformances = await this.attachCompetitionPerformances(withCareerHistory as Player[])
      return withPerformances[0] as Player
    } catch (error) {
      throw new Error('Failed to fetch player')
    }
  }

  async createPlayer(player: any) {
    this.ensureEnabled()
    try {
      const { career_history, competition_performances, youtube, ...playerPayload } = player || {}
      const response = await this.client.post('/players', playerPayload, {
        headers: { 'Prefer': 'return=representation' }
      })
      const createdPlayer = response.data[0]
      if (createdPlayer?.id && Array.isArray(career_history)) {
        try {
          const history = await this.syncCareerHistory(createdPlayer.id, career_history)
          createdPlayer.career_history = this.normalizeCareerHistory(history)
        } catch (historyError: any) {
          if (this.shouldRetryWithoutCareerHistory(historyError)) {
            // Legacy fallback for environments that still rely on the old JSON column.
            try {
              await this.client.patch(`/players?id=eq.${createdPlayer.id}`, { career_history }, {
                headers: { 'Prefer': 'return=representation' }
              })
            } catch {
              // If the legacy column is gone, we still keep the created player.
            }
          } else {
            throw historyError
          }
        }
      }
      if (createdPlayer?.id && Array.isArray(competition_performances)) {
        try {
          const performances = await this.syncCompetitionPerformances(createdPlayer.id, competition_performances)
          createdPlayer.competition_performances = this.normalizeCompetitionPerformances(performances)
        } catch (performanceError: any) {
          if (this.shouldRetryWithoutCompetitionPerformance(performanceError)) {
            try {
              await this.client.patch(`/players?id=eq.${createdPlayer.id}`, { competition_performances }, {
                headers: { 'Prefer': 'return=representation' }
              })
            } catch {
              // Ignore legacy fallback failures; the normalized update already succeeded.
            }
          } else {
            throw performanceError
          }
        }
      }
      // Sync youtube URL to match_videos table
      if (createdPlayer?.id && youtube && typeof youtube === 'string' && youtube.trim()) {
        try {
          const youtubeId = this.extractYouTubeId(youtube)
          await this.client.post('/match_videos', {
            player_id: createdPlayer.id,
            title: 'Player Highlights',
            url: youtube,
            platform: 'youtube',
            thumbnail_url: youtubeId ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg` : null,
            uploaded_by: null,
          }, { headers: { 'Prefer': 'return=representation' } })
        } catch {
          // Non-critical: player still created even if video sync fails
        }
      }
      return createdPlayer
    } catch (error: any) {
      if (this.shouldRetryWithoutCareerHistory(error) || this.shouldRetryWithoutCompetitionPerformance(error)) {
        const fallbackResponse = await this.client.post('/players', this.stripCompetitionPerformance(this.stripCareerHistory(player)), {
          headers: { 'Prefer': 'return=representation' }
        })
        return fallbackResponse.data[0]
      }
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create player: ${msg}`)
    }
  }

  async updatePlayer(id: string, updates: any, options?: { useServiceRole?: boolean }) {
    // When service role is required (e.g. admin editing any player bypassing RLS),
    // route through the backend override-player endpoint — same pattern as deletePlayer.
    if (options?.useServiceRole) {
      const { backendClient } = await import('./backend')
      const { data: { session } } = await backendClient.auth.getSession()
      const accessToken = session?.access_token

      if (!accessToken) throw new Error('No active session')

      const response = await fetch('/api/admin/override-player', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ playerId: id, updates }),
      })

      if (!response.ok) {
        let message = 'Failed to update player'
        try {
          const payload = await response.json()
          if (typeof payload?.error === 'string' && payload.error.trim()) {
            message = payload.error
          }
        } catch { /* ignore JSON parse failures */ }
        throw new Error(message)
      }

      // Return the local updates merged in — the admin endpoint doesn't return
      // the full player row, so we rely on the optimistic store update.
      return updates
    }

    this.ensureEnabled()
    try {
      const client = this.getClient()
      const { career_history, competition_performances, youtube, ...playerUpdates } = updates || {}
      const response = await client.patch(`/players?id=eq.${id}`, playerUpdates, {
        headers: { 'Prefer': 'return=representation' }
      })
      if (!response.data || response.data.length === 0) {
        throw new Error('Update blocked by security policy. You may not have permission to edit this player.')
      }
      const updatedPlayer = response.data[0]
      if (updatedPlayer?.id && Array.isArray(career_history)) {
        try {
          const history = await this.syncCareerHistory(updatedPlayer.id, career_history)
          updatedPlayer.career_history = this.normalizeCareerHistory(history)
        } catch (historyError: any) {
          if (this.shouldRetryWithoutCareerHistory(historyError)) {
            try {
              await client.patch(`/players?id=eq.${updatedPlayer.id}`, { career_history }, {
                headers: { 'Prefer': 'return=representation' }
              })
            } catch {
              // Ignore legacy fallback failures; the normalized update already succeeded.
            }
          } else {
            throw historyError
          }
        }
      }
      if (updatedPlayer?.id && Array.isArray(competition_performances)) {
        try {
          const performances = await this.syncCompetitionPerformances(updatedPlayer.id, competition_performances)
          updatedPlayer.competition_performances = this.normalizeCompetitionPerformances(performances)
        } catch (performanceError: any) {
          if (this.shouldRetryWithoutCompetitionPerformance(performanceError)) {
            try {
              await client.patch(`/players?id=eq.${updatedPlayer.id}`, { competition_performances }, {
                headers: { 'Prefer': 'return=representation' }
              })
            } catch {
              // Ignore legacy fallback failures; the normalized update already succeeded.
            }
          } else {
            throw performanceError
          }
        }
      }
      // Sync youtube URL to match_videos table if changed
      if (updatedPlayer?.id && youtube && typeof youtube === 'string' && youtube.trim()) {
        try {
          // Check if this youtube URL already exists in match_videos for this player
          const existing = await client.get(`/match_videos?player_id=eq.${id}&url=eq.${encodeURIComponent(youtube)}`)
          if (!existing.data || existing.data.length === 0) {
            const youtubeId = this.extractYouTubeId(youtube)
            await client.post('/match_videos', {
              player_id: id,
              title: 'Player Highlights',
              url: youtube,
              platform: 'youtube',
              thumbnail_url: youtubeId ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg` : null,
              uploaded_by: null,
            }, { headers: { 'Prefer': 'return=representation' } })
          }
        } catch {
          // Non-critical: player update still succeeded
        }
      }
      return updatedPlayer
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to update player: ${msg}`)
    }
  }

  async deletePlayer(id: string, options?: { useServiceRole?: boolean }) {
    if (options?.useServiceRole) {
      const { backendClient } = await import('./backend')
      const { data: { session } } = await backendClient.auth.getSession()
      const accessToken = session?.access_token

      if (!accessToken) {
        throw new Error('No active session')
      }

      const response = await fetch('/api/admin/delete-player', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ playerId: id }),
      })

      if (!response.ok) {
        let message = 'Failed to delete player'
        try {
          const payload = await response.json()
          if (typeof payload?.error === 'string' && payload.error.trim()) {
            message = payload.error
          }
        } catch {
          // Ignore JSON parsing issues and use the default error message.
        }
        throw new Error(message)
      }

      return true
    }

    this.ensureEnabled()
    try {
      await this.client.delete(`/players?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete player')
    }
  }

  // Profiles (Accounts)
  async getProfiles() {
    this.ensureEnabled()
    try {
      const response = await this.client.get('/profiles?order=created_at.desc')
      return response.data
    } catch (error) {
      throw new Error('Failed to fetch profiles')
    }
  }

  async getProfile(userId: string) {
    this.ensureEnabled()
    try {
      const response = await this.client.get(`/profiles?id=eq.${userId}`)
      return response.data[0]
    } catch (error) {
      throw new Error('Failed to fetch profile')
    }
  }

  async getProfilesByIds(ids: string[]) {
    this.ensureEnabled()
    if (ids.length === 0) return []
    try {
      const unique = [...new Set(ids)]
      const response = await this.client.get(`/profiles?id=in.(${unique.join(',')})`)
      return response.data
    } catch (error) {
      throw new Error('Failed to fetch profiles')
    }
  }

  async updateProfile(id: string, updates: any) {
    this.ensureEnabled()
    try {
      const client = this.getClient()
      const response = await client.patch(`/profiles?id=eq.${id}`, updates, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to update profile: ${msg}`)
    }
  }

  async deleteProfile(id: string) {
    this.ensureEnabled()
    try {
      const client = this.getClient()
      await client.delete(`/profiles?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete profile')
    }
  }

  // Scouts
  async getScouts() {
    this.ensureEnabled()
    try {
      const response = await this.client.get('/scouts')
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch scouts')
    }
  }

  async verifyScout(scoutId: string) {
    this.ensureEnabled()
    try {
      const response = await this.client.patch(`/scouts?id=eq.${scoutId}`, { verified: true })
      return response.data
    } catch (error) {
      throw new Error('Failed to verify scout')
    }
  }

  // Clubs
  async getClubs() {
    this.ensureEnabled()
    try {
      const response = await this.client.get('/clubs')
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch clubs')
    }
  }

  async createClub(club: any) {
    this.ensureEnabled()
    try {
      const client = this.getClient()
      const payload = {
        id: club?.id,
        name: club?.name,
        location: club?.location || null,
        logo_url: club?.logoUrl || club?.logo_url || null,
        competitions: Array.isArray(club?.competitions) ? club.competitions : [],
        scout_id: this.isUuid(club?.scout_id) ? club.scout_id : (this.isUuid(club?.scoutId) ? club.scoutId : null),
        is_verified: club?.is_verified ?? false,
        is_visible: club?.is_visible ?? true,
      }
      const response = await client.post('/clubs', payload, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create club: ${msg}`)
    }
  }

  async updateClub(id: string, updates: any) {
    this.ensureEnabled()
    try {
      const client = this.getClient()
      const payload = {
        ...(updates?.name !== undefined ? { name: updates.name } : {}),
        ...(updates?.location !== undefined ? { location: updates.location } : {}),
        ...(updates?.logoUrl !== undefined ? { logo_url: updates.logoUrl } : {}),
        ...(updates?.logo_url !== undefined ? { logo_url: updates.logo_url } : {}),
        ...(updates?.competitions !== undefined ? { competitions: Array.isArray(updates.competitions) ? updates.competitions : [] } : {}),
        ...(updates?.scoutId !== undefined ? { scout_id: updates.scoutId } : {}),
        ...(updates?.scout_id !== undefined ? { scout_id: updates.scout_id } : {}),
        ...(updates?.is_verified !== undefined ? { is_verified: updates.is_verified } : {}),
        ...(updates?.is_visible !== undefined ? { is_visible: updates.is_visible } : {}),
      }
      const response = await client.patch(`/clubs?id=eq.${id}`, payload, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to update club: ${msg}`)
    }
  }

  async deleteClub(id: string) {
    this.ensureEnabled()
    try {
      const client = this.getClient()
      await client.delete(`/clubs?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete club')
    }
  }

  // Club Staff
  async getClubStaff(clubId: string) {
    this.ensureEnabled()
    try {
      const response = await this.client.get(`/club_staff?club_id=eq.${clubId}&order=created_at.desc`)
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch staff')
    }
  }

  async getStaffMember(id: string) {
    this.ensureEnabled()
    try {
      const response = await this.client.get(`/club_staff?id=eq.${id}`)
      return Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null
    } catch (error) {
      throw new Error('Failed to fetch staff member')
    }
  }

  async createStaff(staff: any) {
    this.ensureEnabled()
    try {
      const client = this.getClient()
      const payload = {
        club_id: staff?.club_id,
        full_name: staff?.full_name,
        photo_url: staff?.photo_url || null,
        email: staff?.email || null,
        phone: staff?.phone || null,
        emergency_contact_name: staff?.emergency_contact_name || null,
        emergency_contact_phone: staff?.emergency_contact_phone || null,
        position: staff?.position,
        employment_status: staff?.employment_status || 'active',
        contract_type: staff?.contract_type || null,
        start_date: staff?.start_date || null,
        end_date: staff?.end_date || null,
        notes: staff?.notes || null,
      }
      const response = await client.post('/club_staff', payload, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to add staff: ${msg}`)
    }
  }

  async updateStaff(id: string, updates: any) {
    this.ensureEnabled()
    try {
      const client = this.getClient()
      const payload = {
        ...(updates?.full_name !== undefined ? { full_name: updates.full_name } : {}),
        ...(updates?.photo_url !== undefined ? { photo_url: updates.photo_url } : {}),
        ...(updates?.email !== undefined ? { email: updates.email } : {}),
        ...(updates?.phone !== undefined ? { phone: updates.phone } : {}),
        ...(updates?.emergency_contact_name !== undefined ? { emergency_contact_name: updates.emergency_contact_name } : {}),
        ...(updates?.emergency_contact_phone !== undefined ? { emergency_contact_phone: updates.emergency_contact_phone } : {}),
        ...(updates?.position !== undefined ? { position: updates.position } : {}),
        ...(updates?.employment_status !== undefined ? { employment_status: updates.employment_status } : {}),
        ...(updates?.contract_type !== undefined ? { contract_type: updates.contract_type } : {}),
        ...(updates?.start_date !== undefined ? { start_date: updates.start_date } : {}),
        ...(updates?.end_date !== undefined ? { end_date: updates.end_date } : {}),
        ...(updates?.notes !== undefined ? { notes: updates.notes } : {}),
      }
      const response = await client.patch(`/club_staff?id=eq.${id}`, payload, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to update staff: ${msg}`)
    }
  }

  async deleteStaff(id: string) {
    this.ensureEnabled()
    try {
      const client = this.getClient()
      await client.delete(`/club_staff?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete staff member')
    }
  }

  // Check if scout can edit player
  canScoutEdit(scoutName: string, playerScout: string, userRole: string): boolean {
    return userRole === 'admin' || scoutName === playerScout
  }

  // Announcements
  async getAnnouncements() {
    try {
      const response = await this.client.get('/announcements?order=created_at.desc')
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch announcements')
    }
  }

  async createAnnouncement(announcement: any) {
    try {
      const response = await this.client.post('/announcements', announcement, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create announcement: ${msg}`)
    }
  }

  async updateAnnouncement(id: string, updates: any) {
    try {
      const response = await this.client.patch(`/announcements?id=eq.${id}`, updates, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to update announcement: ${msg}`)
    }
  }

  async deleteAnnouncement(id: string) {
    try {
      await this.client.delete(`/announcements?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete announcement')
    }
  }

  // Notifications (legacy announcement-based)
  async getNotifications() {
    try {
      const response = await this.client.get('/notifications?order=created_at.desc')
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch notifications')
    }
  }

  async markNotificationAsRead(id: string) {
    try {
      await this.client.patch(`/notifications?id=eq.${id}`, { is_read: true })
      return true
    } catch (error) {
      throw new Error('Failed to update notification')
    }
  }

  // Player Notifications (comprehensive system)
  async getPlayerNotifications(playerId: string) {
    try {
      const response = await this.client.get(`/player_notifications?player_id=eq.${playerId}&order=created_at.desc`)
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch player notifications')
    }
  }

  async createPlayerNotification(notification: {
    player_id: string
    type: string
    title: string
    message: string
    actor_name?: string
    actor_role?: string
    actor_id?: string
    link?: string
    metadata?: Record<string, any>
  }) {
    try {
      const response = await this.client.post('/player_notifications', notification, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create notification: ${msg}`)
    }
  }

  async markPlayerNotificationAsRead(id: string) {
    try {
      await this.client.patch(`/player_notifications?id=eq.${id}`, { is_read: true })
      return true
    } catch (error) {
      throw new Error('Failed to mark notification as read')
    }
  }

  async markAllPlayerNotificationsAsRead(playerId: string) {
    try {
      await this.client.patch(`/player_notifications?player_id=eq.${playerId}&is_read=eq.false`, { is_read: true })
      return true
    } catch (error) {
      throw new Error('Failed to mark all notifications as read')
    }
  }

  async deletePlayerNotification(id: string) {
    try {
      await this.client.delete(`/player_notifications?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete notification')
    }
  }

  async getUnreadNotificationCount(playerId: string) {
    try {
      const response = await this.client.get(`/player_notifications?player_id=eq.${playerId}&is_read=eq.false&select=id`, {
        headers: { 'Prefer': 'count=exact' }
      })
      return response.headers?.['content-range']?.split('/')[1] || 0
    } catch (error) {
      return 0
    }
  }

  // Match Videos
  async getMatchVideos(playerId: string) {
    try {
      const response = await this.client.get(`/match_videos?player_id=eq.${playerId}&order=created_at.desc`)
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch match videos')
    }
  }

  async createMatchVideo(video: any) {
    try {
      const { goals, assists, shots, key_passes, dribbles, tackles, rating, player_id, ...videoFields } = video
      let performanceId: string | null = null

      // If any stat is provided, create a competition_performance entry
      const hasStats = [goals, assists, shots, key_passes, dribbles, tackles, rating].some(
        v => v !== undefined && v !== null && v !== ''
      )

      if (hasStats && player_id) {
        const { backendClient } = await import('./backend')
        const { data: { user } } = await backendClient.auth.getUser()
        const perfRow = {
          id: crypto.randomUUID(),
          player_id,
          competition_name: videoFields.competition || 'Unknown Competition',
          appearance_date: videoFields.match_date || null,
          opponent: videoFields.opponent || null,
          minutes: videoFields.minutes_played || 0,
          goals: goals || 0,
          assists: assists || 0,
          shots: shots ?? null,
          key_passes: key_passes ?? null,
          dribbles: dribbles ?? null,
          tackles: tackles ?? null,
          rating: rating ?? null,
          created_by: user?.id || null,
          updated_at: new Date().toISOString(),
        }
        const perfResponse = await this.client.post('/player_competition_performances', perfRow, {
          headers: { 'Prefer': 'return=representation' }
        })
        performanceId = perfResponse.data?.[0]?.id || perfRow.id
      }

      const payload = { ...videoFields, player_id, performance_id: performanceId }
      const response = await this.client.post('/match_videos', payload, {
        headers: { 'Prefer': 'return=representation' }
      })
      return { ...response.data[0], performance_id: performanceId }
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create match video: ${msg}`)
    }
  }

  async deleteMatchVideo(id: string) {
    try {
      // Fetch the video first to check for linked performance
      const { data: videos } = await this.client.get(`/match_videos?id=eq.${id}`)
      const video = videos?.[0]

      await this.client.delete(`/match_videos?id=eq.${id}`)

      // Delete linked competition performance if it exists
      if (video?.performance_id) {
        try {
          await this.client.delete(`/player_competition_performances?id=eq.${video.performance_id}`)
        } catch {
          // Performance may already be deleted or policy may block — don't fail the whole operation
        }
      }
      return true
    } catch (error) {
      throw new Error('Failed to delete match video')
    }
  }

  // Achievements
  async getAchievements(playerId: string) {
    try {
      const response = await this.client.get(`/achievements?player_id=eq.${playerId}&order=year.desc,created_at.desc`)
      return response.data
    } catch (error) {
      throw new Error('Failed to fetch achievements')
    }
  }

  async createAchievement(achievement: any) {
    try {
      const response = await this.client.post('/achievements', achievement, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create achievement: ${msg}`)
    }
  }

  async deleteAchievement(id: string) {
    try {
      await this.client.delete(`/achievements?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete achievement')
    }
  }

  // Player Ratings
  async getPlayerRatings(playerId: string) {
    try {
      const response = await this.client.get(
        `/player_ratings?player_id=eq.${playerId}&order=created_at.desc`
      )
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      console.error('Failed to fetch player ratings:', error)
      return []
    }
  }

  async getAverageRating(playerId: string): Promise<number> {
    try {
      const response = await this.client.get(
        `/player_ratings?select=rating&player_id=eq.${playerId}`
      )
      const ratings = Array.isArray(response.data) ? response.data : []
      if (ratings.length === 0) return 0
      const sum = ratings.reduce((acc: number, r: any) => acc + Number(r.rating), 0)
      return Math.round((sum / ratings.length) * 10) / 10
    } catch (error) {
      console.error('Failed to fetch average rating:', error)
      return 0
    }
  }

  async getPlayerRatingByRater(playerId: string, raterId: string) {
    try {
      const response = await this.client.get(
        `/player_ratings?player_id=eq.${playerId}&rater_id=eq.${raterId}&select=*`
      )
      return response.data?.[0] || null
    } catch (error) {
      console.error('Failed to fetch player rating:', error)
      return null
    }
  }

  async upsertPlayerRating(rating: { player_id: string; rater_id: string; rater_role: string; rating: number; comment?: string }) {
    try {
      const response = await this.client.post('/player_ratings', rating, {
        headers: { 'Prefer': 'resolution=merge-duplicates,return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to save rating: ${msg}`)
    }
  }

  async deletePlayerRating(id: string) {
    try {
      await this.client.delete(`/player_ratings?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete rating')
    }
  }

  async getTopRatedPlayers(minRating: number = 8.5, limit: number = 50) {
    try {
      // Get average ratings per player, filtered by min rating
      const response = await this.client.get(
        `/player_ratings?select=player_id,rating&rating=gte.${minRating}`
      )
      const ratings = Array.isArray(response.data) ? response.data : []

      // Group by player_id and compute averages
      const grouped: Record<string, number[]> = {}
      ratings.forEach((r: any) => {
        if (!grouped[r.player_id]) grouped[r.player_id] = []
        grouped[r.player_id].push(Number(r.rating))
      })

      const averages = Object.entries(grouped)
        .map(([playerId, rats]) => ({
          player_id: playerId,
          average: Math.round((rats.reduce((a, b) => a + b, 0) / rats.length) * 10) / 10,
          count: rats.length,
        }))
        .filter(r => r.average >= minRating)
        .sort((a, b) => b.average - a.average || b.count - a.count)
        .slice(0, limit)

      return averages
    } catch (error) {
      console.error('Failed to fetch top rated players:', error)
      return []
    }
  }

  // Messaging
  async getConversations(playerId: string) {
    try {
      const response = await this.client.get(`/conversations?player_id=eq.${playerId}&order=last_message_at.desc.nullslast`)
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch conversations')
    }
  }

  private async fetchConversationRowsByRecipient(recipientId: string) {
    const candidateFields = ['scout_id', 'other_user_id'] as const

    for (const field of candidateFields) {
      try {
        const response = await this.client.get(`/conversations?${field}=eq.${recipientId}&order=last_message_at.desc.nullslast`)
        return Array.isArray(response.data) ? response.data : []
      } catch (error: any) {
        const message = `${error?.response?.data?.message || error?.message || ''}`.toLowerCase()
        const isMissingColumnError = message.includes('does not exist') || message.includes('column') || message.includes('bad request')
        if (!isMissingColumnError) {
          throw error
        }
      }
    }

    return []
  }

  private async fetchConversationIdsByRecipient(recipientId: string) {
    const candidateFields = ['scout_id', 'other_user_id'] as const

    for (const field of candidateFields) {
      try {
        const response = await this.client.get(`/conversations?select=id&${field}=eq.${recipientId}`)
        const rows = Array.isArray(response.data) ? response.data : []
        if (rows.length > 0 || field === 'other_user_id') {
          return rows.map((row: any) => row.id)
        }
      } catch (error: any) {
        const message = `${error?.response?.data?.message || error?.message || ''}`.toLowerCase()
        const isMissingColumnError = message.includes('does not exist') || message.includes('column') || message.includes('bad request')
        if (!isMissingColumnError) {
          throw error
        }
      }
    }

    return []
  }

  async getConversationsByRecipient(recipientId: string) {
    try {
      return await this.fetchConversationRowsByRecipient(recipientId)
    } catch (error) {
      throw new Error('Failed to fetch conversations')
    }
  }

  async createConversation(conversation: any) {
    try {
      const response = await this.client.post('/conversations', conversation, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create conversation: ${msg}`)
    }
  }

  async updateConversation(id: string, updates: any) {
    try {
      await this.client.patch(`/conversations?id=eq.${id}`, updates)
      return true
    } catch (error) {
      throw new Error('Failed to update conversation')
    }
  }

  async deleteConversation(id: string) {
    try {
      await this.client.delete(`/conversations?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete conversation')
    }
  }

  async getMessages(conversationId: string) {
    try {
      const response = await this.client.get(`/messages?conversation_id=eq.${conversationId}&order=created_at.asc`)
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch messages')
    }
  }

  async sendMessage(message: any) {
    try {
      const response = await this.client.post('/messages', message, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to send message: ${msg}`)
    }
  }

  async markMessagesAsRead(conversationId: string) {
    try {
      // Fetch unread messages first, then mark each
      const unread = await this.client.get(
        `/messages?conversation_id=eq.${conversationId}&is_read=eq.false&select=id`
      )
      if (unread.data && unread.data.length > 0) {
        const ids = unread.data.map((m: any) => m.id)
        // Mark in batches — Supabase supports in filter
        await this.client.patch(
          `/messages?id=in.(${ids.join(',')})`,
          { is_read: true },
          { headers: { 'Prefer': 'return=minimal' } }
        )
      }
      return true
    } catch (error) {
      console.error('Failed to mark messages as read:', error)
      return false
    }
  }

  async getUnreadCountForPlayer(playerId: string, authUserId?: string) {
    try {
      // Step 1: Get conversation IDs for this player
      const convResponse = await this.client.get(
        `/conversations?select=id&player_id=eq.${playerId}`
      )
      const convIds = (Array.isArray(convResponse.data) ? convResponse.data : []).map((c: any) => c.id)
      if (convIds.length === 0) return 0

      // Step 2: Count unread messages NOT sent by this user (use auth.uid, not player record ID)
      const senderFilter = authUserId
        ? `&sender_id=neq.${authUserId}`
        : ''
      const response = await this.client.get(
        `/messages?select=id&conversation_id=in.(${convIds.join(',')})${senderFilter}&is_read=eq.false`
      )
      return response.data?.length || 0
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
      return 0
    }
  }

  async getUnreadCountByRecipient(recipientId: string) {
    try {
      const convIds = await this.fetchConversationIdsByRecipient(recipientId)
      if (convIds.length === 0) return 0

      const response = await this.client.get(
        `/messages?select=id&conversation_id=in.(${convIds.join(',')})&sender_id=neq.${recipientId}&is_read=eq.false`
      )
      return Array.isArray(response.data) ? response.data.length : 0
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
      return 0
    }
  }

  async reportMessage(messageId: string) {
    try {
      await this.client.patch(`/messages?id=eq.${messageId}`, { is_reported: true })
      return true
    } catch (error) {
      throw new Error('Failed to report message')
    }
  }

  // Trial Invitations
  async getTrialInvitations(playerId: string) {
    try {
      const response = await this.client.get(`/trial_invitations?player_id=eq.${playerId}&order=trial_date.desc`)
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch trial invitations')
    }
  }

  async respondToTrialInvitation(id: string, status: 'accepted' | 'declined') {
    try {
      await this.client.patch(`/trial_invitations?id=eq.${id}`, { status })
      return true
    } catch (error) {
      throw new Error('Failed to respond to trial invitation')
    }
  }

  async createTrialInvitation(invitation: any) {
    try {
      const response = await this.client.post('/trial_invitations', invitation, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create trial invitation: ${msg}`)
    }
  }

  // Match Reports
  async createMatchReport(report: any) {
    try {
      const response = await this.client.post('/match_reports', report, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create match report: ${msg}`)
    }
  }

  async getMatchReports(clubId: string) {
    try {
      const response = await this.client.get(`/match_reports?club_id=eq.${clubId}&order=date.desc`)
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch match reports')
    }
  }

  // Competitions
  async getCompetitions() {
    this.ensureEnabled()
    try {
      const response = await this.client.get('/competitions?order=updated_at.desc.nullslast,created_at.desc')
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch competitions')
    }
  }

  async createCompetition(competition: Competition) {
    this.ensureEnabled()
    try {
      const response = await this.client.post('/competitions', competition, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create competition: ${msg}`)
    }
  }

  async updateCompetition(id: string, updates: Partial<Competition>) {
    this.ensureEnabled()
    try {
      const response = await this.client.patch(`/competitions?id=eq.${id}`, updates, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to update competition: ${msg}`)
    }
  }

  async deleteCompetition(id: string) {
    this.ensureEnabled()
    try {
      await this.client.delete(`/competitions?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete competition')
    }
  }

  async getCompetitionFixtures(competitionId?: string) {
    this.ensureEnabled()
    try {
      const query = competitionId
        ? `/competition_fixtures?competition_id=eq.${competitionId}&order=fixture_date.desc`
        : '/competition_fixtures?order=fixture_date.desc'
      const response = await this.client.get(query)
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch competition fixtures')
    }
  }

  async createCompetitionFixture(fixture: CompetitionFixture) {
    this.ensureEnabled()
    try {
      const response = await this.client.post('/competition_fixtures', fixture, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create competition fixture: ${msg}`)
    }
  }

  async updateCompetitionFixture(id: string, updates: Partial<CompetitionFixture>) {
    this.ensureEnabled()
    try {
      const response = await this.client.patch(`/competition_fixtures?id=eq.${id}`, updates, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to update competition fixture: ${msg}`)
    }
  }

  async getBlogFixtures() {
    this.ensureEnabled()
    try {
      const response = await this.client.get('/blog_fixtures?order=date.desc')
      return Array.isArray(response.data) ? response.data : []
    } catch {
      throw new Error('Failed to fetch blog fixtures')
    }
  }

  async createBlogFixture(fixture: Fixture) {
    this.ensureEnabled()
    try {
      const response = await this.client.post('/blog_fixtures', fixture, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create blog fixture: ${msg}`)
    }
  }

  async syncBlogFixtures(fixtures: Fixture[]) {
    this.ensureEnabled()
    try {
      const response = await this.client.post('/blog_fixtures?on_conflict=id', fixtures, {
        headers: { 'Prefer': 'resolution=merge-duplicates,return=representation' }
      })
      return Array.isArray(response.data) ? response.data : []
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to sync saved blog fixtures: ${msg}`)
    }
  }

  async updateBlogFixture(id: string, updates: Partial<Fixture>) {
    this.ensureEnabled()
    try {
      const response = await this.client.patch(`/blog_fixtures?id=eq.${id}`, updates, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to update blog fixture: ${msg}`)
    }
  }

  async deleteBlogFixture(id: string) {
    this.ensureEnabled()
    try {
      await this.client.delete(`/blog_fixtures?id=eq.${id}`)
      return true
    } catch {
      throw new Error('Failed to delete blog fixture')
    }
  }

  async deleteCompetitionFixture(id: string) {
    this.ensureEnabled()
    try {
      await this.client.delete(`/competition_fixtures?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete competition fixture')
    }
  }

  // Blog Posts
  async getBlogPosts(publishedOnly = false) {
    try {
      const filter = publishedOnly ? '?is_published=eq.true&order=published_at.desc' : '?order=published_at.desc'
      const response = await this.client.get(`/blog_posts${filter}`)
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      throw new Error('Failed to fetch blog posts')
    }
  }

  async syncBlogPosts(posts: BlogPost[]) {
    try {
      const response = await this.client.post('/blog_posts?on_conflict=id', posts, {
        headers: { 'Prefer': 'resolution=merge-duplicates,return=representation' }
      })
      return Array.isArray(response.data) ? response.data : []
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to sync saved blog posts: ${msg}`)
    }
  }

  async createBlogPost(post: any) {
    try {
      const response = await this.client.post('/blog_posts', post, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to create blog post: ${msg}`)
    }
  }

  async updateBlogPost(id: string, updates: any) {
    try {
      const response = await this.client.patch(`/blog_posts?id=eq.${id}`, updates, {
        headers: { 'Prefer': 'return=representation' }
      })
      return response.data[0]
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message
      throw new Error(`Failed to update blog post: ${msg}`)
    }
  }

  async deleteBlogPost(id: string) {
    try {
      await this.client.delete(`/blog_posts?id=eq.${id}`)
      return true
    } catch (error) {
      throw new Error('Failed to delete blog post')
    }
  }
}

export const apiClient = new APIClient()
